import { useMutation } from '@apollo/client';

import GetVariableContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import {
  PauseContractLines,
  PauseContractLinesVariables
} from '@/graphql/contracts/_generated_/pauseContractLines.mutation';
import PauseContractLineMutation from '@/graphql/contracts/pauseContractLines.mutation.gql';
import { notificationService } from '@/services/notification/notification.client';
import { ALL_ITEMS } from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import { ContractBillingStatusPascalCaseEnum } from '@/graphql/contracts/_generated_/contracts';
import { triggerLineRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { triggerVariableRefresh } from 
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';

export type PauseContractLineParams = PauseContractLinesVariables;

export default function usePauseContractLine() {
  const [_pauseContractLine, { loading, error }] = useMutation<PauseContractLines, PauseContractLinesVariables>(
    PauseContractLineMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        const pausedCount = data.pauseContractLines.length;

        // Check if any lines have FuturePause status
        const hasFuturePause = data.pauseContractLines.some(
          line => line.lineBilling?.status === ContractBillingStatusPascalCaseEnum.FuturePause
        );

        const message = hasFuturePause
          ? `${pausedCount} contract line${pausedCount > 1 ? 's have' : ' has'} been successfully 
          scheduled for future pause`
          : `${pausedCount} contract line${pausedCount > 1 ? 's have' : ' has'} been successfully paused`;

        notificationService.open({
          title: 'Pause contract lines',
          text: message,
          type: 'success'
        });
        
        // Trigger refresh for both BC and Variable lines
        triggerLineRefresh();
        triggerVariableRefresh();
      }
    }
  );

  const pauseContractLine = async (
    inputParams: PauseContractLineParams,
    includeRegularLines: boolean = true,
    includeVariableLines: boolean = false
  ) => {
    try {
      const contractIds = new Set<string>();
      
      // Collect all unique contract IDs
      if (inputParams.contracts) {
        const contractsArray = Array.isArray(inputParams.contracts) 
          ? inputParams.contracts 
          : [inputParams.contracts];
        contractsArray.forEach((contract: { contractId: string }) => {
          contractIds.add(contract.contractId);
        });
      }

      // Build refetch queries array based on line type flags
      const refetchQueries: any[] = [];

      // Refetch BC lines if regular lines are included
      if (includeRegularLines) {
        const contractIdsArray = Array.from(contractIds);
        refetchQueries.push({
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: contractIdsArray.length === 1 
                ? { eq: contractIdsArray[0] }
                : { containsValues: contractIdsArray }
            },
            first: ALL_ITEMS
          }
        });
      }

      // Include variable lines query if variable lines are included
      if (includeVariableLines) {
        const contractIdsArray = Array.from(contractIds);
        refetchQueries.push({
          query: GetVariableContractLinesQuery,
          variables: {
            filter: {
              headerPrimaryKey: contractIdsArray.length === 1 
                ? { eq: contractIdsArray[0] }
                : { containsValues: contractIdsArray }
            },
            first: ALL_ITEMS
          }
        });
      }

      const result = await _pauseContractLine({
        variables: inputParams,
        refetchQueries
      });

      if (result.data?.pauseContractLines) {
        return true;
      } else {
        return false;
      }
    } catch (pauseError) {
      return false;
    }
  };

  return {
    pauseContractLine,
    isLoadingPauseContractLine: loading,
    errorPauseContractLine: error,
  } as const;
}
